const EVENTS = [
  { id: "BATCH_START",    label: "Batch start",         desc: "New processing run initiated — timestamp, source files, and run ID recorded",             color: "text-cyan-300"    },
  { id: "SOURCE_READ",    label: "Source file read",    desc: "Source file loaded — SHA-256 hash captured for integrity tracking",                       color: "text-sky-300"     },
  { id: "INSERT",         label: "Value inserted",      desc: "Title Code resolved and value written to master — source, target row, and value logged",   color: "text-emerald-300" },
  { id: "UPDATE",         label: "Value updated",       desc: "Existing master value changed — old and new values both retained in audit record",          color: "text-teal-300"    },
  { id: "CALC",           label: "Calculation run",     desc: "APP+WEB combination or other derived value computed — inputs and result logged",            color: "text-violet-300"  },
  { id: "FORMULA_SKIP",   label: "Formula cell skipped",desc: "Target cell contained a BB formula — insertion suppressed, event logged with cell ref",    color: "text-amber-300"   },
  { id: "REVIEW_CREATE",  label: "Review item created", desc: "Conflict or unresolved rule — source value, target row, Title Code, and rule recorded",    color: "text-yellow-300"  },
  { id: "REVIEW_DECIDE",  label: "Review decision",     desc: "Human Insert or Discard decision recorded with reviewer and timestamp",                    color: "text-orange-300"  },
  { id: "ERROR",          label: "Processing error",    desc: "Exception or unexpected state — full stack and context captured for investigation",         color: "text-red-300"     },
  { id: "BATCH_END",      label: "Batch complete",      desc: "Run finalised — inserted/updated/unchanged/skipped counts, archive path, and duration",    color: "text-slate-300"   },
];

const REVIEW_STEPS = [
  { num: "01", title: "Conflict detected",    desc: "Source value cannot be auto-resolved — ambiguity, multiple candidates, or an N/A requiring business judgement." },
  { num: "02", title: "Review item created",  desc: "Platform creates a review record containing: source value, target row, Title Code, conflict rule, and processing context." },
  { num: "03", title: "Review centre display",desc: "Operator sees the review item with full context — what was found, what the current master value is, and which rule triggered the conflict." },
  { num: "04", title: "Insert or Discard",    desc: "Operator chooses to Insert the source value into the master, or Discard it — leaving the existing value unchanged." },
  { num: "05", title: "Decision recorded",    desc: "The Insert or Discard decision is written to the audit database with the operator identity and timestamp. Decision history is permanent." },
];

export default function AuditWorkflow() {
  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">AUDIT & REVIEW</p>
        <h4 className="mt-1 text-sm font-semibold text-white">Review workflow + audit event log</h4>
        <p className="mt-1 text-xs text-slate-400">
          Every processing run produces an immutable SQLite audit trail. Conflicting records go to a human-in-the-loop review queue instead of being silently resolved — decisions are permanently recorded.
        </p>
      </div>

      {/* Review workflow */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
        <p className="mb-3 font-mono text-[10px] tracking-[0.15em] text-amber-400">REVIEW QUEUE WORKFLOW</p>
        <div className="relative space-y-3 pl-4">
          <div className="absolute left-0 top-1 h-[calc(100%-8px)] w-px bg-amber-500/30" />
          {REVIEW_STEPS.map((s) => (
            <div key={s.num} className="relative">
              <div className="absolute -left-4 top-1.5 h-2 w-2 rounded-full bg-amber-400" />
              <p className="text-xs font-semibold text-slate-200">{s.title}</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Audit events */}
      <div className="rounded-xl border border-white/10 bg-black/20 p-3">
        <p className="mb-2 font-mono text-[9px] tracking-[0.15em] text-slate-500">AUDIT EVENT TYPES — SQLite</p>
        <div className="space-y-1.5">
          {EVENTS.map(({ id, label, desc, color }) => (
            <div key={id} className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
              <span className={`min-w-[120px] font-mono text-[9px] font-bold ${color}`}>{id}</span>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-slate-200">{label}</p>
                <p className="text-[10px] text-slate-500">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Output */}
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
        <p className="font-mono text-[10px] tracking-[0.2em] text-emerald-400">OUTPUT & ARCHIVE</p>
        <ul className="mt-2 space-y-1">
          {[
            "Processed master workbook — updated reporting period, all inserted and calculated values",
            "Processing event log — full SQLite record of every event in the run",
            "Review queue — open items awaiting human decision",
            "Audit database — all historical runs, source hashes, decisions, and events",
            "Archived processing run — master + logs bundled with run metadata for historical reference",
            "CSV-ready output — downstream Bangladesh Bank submission preparation",
          ].map((o) => (
            <li key={o} className="flex gap-1.5 text-[11px] text-slate-300">
              <span className="mt-0.5 shrink-0 text-emerald-400">›</span>
              <span>{o}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
